import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Subject, firstValueFrom, from } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  finalize,
  takeUntil
} from 'rxjs/operators';
import {
  CollectionEvent,
  DC_REGIONS,
  DcEventConfigSource,
  DcRoster,
  DcSubtypeCollectionEventConfig,
  DcSubtypeConfig,
  DcTypeConfig
} from '../../domain/dc.roster.model';
import { TaskStatus } from '../../domain/collector-task.model';
import { CollectorEligibilityContext, CollectorUser } from '../../domain/user';
import { AlertService } from '../../services/alert.service';
import { DcRosterService } from '../../services/dc-roster.service';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../auth/auth.service';

const LIVE_CRITICAL_DC_TYPES = new Set(['EVL', 'PVL']);
const BILLING_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const DC_SUBTYPE_REQUIRED_WARNING =
  'DC roster changes were not saved because DC Subtype is required when a DC Type is selected.';
const UNSAVED_ROSTER_REFRESH_WARNING =
  'DC roster was not refreshed because it contains unsaved changes. Apply or save the changes first.';

export type DcRosterEventColumn =
  | 'code'
  | 'duration'
  | 'critical'
  | 'collector'
  | 'dcTypeOverride'
  | 'status'
  | 'actions';

export interface DcRosterAssignmentSummary {
  rosterTotal: number;
  rosterAssigned: number;
}

export interface CollectionEventOption {
  code: string;
  eventGrade: number | null;
  effort: number | null;
  collectionType: string | null;
  source: DcEventConfigSource;
}

@Component({
  selector: 'app-dc-roster',
  templateUrl: './dc-roster.component.html',
  styleUrls: ['./dc-roster.component.css']
})
export class DcRosterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() gameId?: string | null;
  @Input() readOnly = false;
  @Input() showRosterFields = true;
  @Input() showAddCollectionEvent = true;
  @Input() eventColumns: DcRosterEventColumn[] = [
    'code',
    'duration',
    'critical',
    'collector',
    'dcTypeOverride',
    'status',
    'actions'
  ];
  @Output() rosterSummaryChange = new EventEmitter<DcRosterAssignmentSummary>();

  readonly dcRegions = DC_REGIONS;
  readonly criticalOptions = [0.1, 0.2, 0.3];
  readonly statusLabels: Record<TaskStatus, string> = {
    yes: 'Yes',
    no: 'No',
    in_progress: 'In Progress',
    exchange: 'Exchange',
    done: 'Done',
    open: 'Open'
  };

  dcTypes: DcTypeConfig[] = [];
  collectionEventOptions: CollectionEventOption[] = [];
  filteredCollectionEventOptions: CollectionEventOption[] = [];
  private dcTypeDescriptions = new Map<string, string>();
  dcRoster: DcRoster = this.createEmptyDcRoster();
  collectorNames = new Map<string, string>();
  selectedCollectionEventOption: CollectionEventOption | string | null = null;
  isAutosavePending = false;
  isInitializing = true;

  hasWritePermission = false;
  @Input() saveMode: 'manual' | 'immediate' = 'manual';

  private readonly autosaveTrigger$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();
  private hasUnsavedChanges = false;

  get isReadOnly(): boolean {
    return this.readOnly || !this.hasWritePermission;
  }

  get isBusy(): boolean {
    return this.isInitializing || this.isAutosavePending;
  }

  get isBillingMonthValid(): boolean {
    return (
      !this.dcRoster.billingMonth ||
      BILLING_MONTH_PATTERN.test(this.dcRoster.billingMonth)
    );
  }

  get shouldShowCollectionEventsTable(): boolean {
    return (
      this.dcRoster.collectionEvents.length > 0 || !!this.dcRoster.dcSubtype
    );
  }

  shouldShowEventColumn(column: DcRosterEventColumn): boolean {
    return this.eventColumns.includes(column);
  }

  supportsCritical(event: CollectionEvent): boolean {
    const effectiveDcType = event.dcTypeOverride ?? this.dcRoster.dcType;

    return LIVE_CRITICAL_DC_TYPES.has(
      `${effectiveDcType ?? ''}`.trim().toUpperCase()
    );
  }

  constructor(
    private dcRosterService: DcRosterService,
    private userService: UserService,
    private alertService: AlertService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    try {
      this.hasWritePermission = await firstValueFrom(
        this.authService.hasPermission('write:dc-roster')
      );
      this.setupAutosave();
      await this.loadDcRosterConfig();
    } finally {
      this.isInitializing = false;
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!changes.gameId) {
      return;
    }

    const nextGameId = changes.gameId.currentValue as string | null;
    if (nextGameId) {
      await this.loadDcRoster(nextGameId);
      return;
    }

    this.dcRoster = this.createEmptyDcRoster('');
    this.collectorNames.clear();
    this.clearCollectionEventSearch();
    this.emitRosterSummary();
  }

  clearRoster(): void {
    this.dcRoster = this.createEmptyDcRoster();
    this.hasUnsavedChanges = false;
    this.collectorNames.clear();
    this.clearCollectionEventSearch();
    this.emitRosterSummary();
  }

  async refresh(): Promise<void> {
    if (this.hasUnsavedChanges) {
      this.alertService.showWarning(UNSAVED_ROSTER_REFRESH_WARNING);
      return;
    }

    if (this.gameId) {
      await this.loadDcRoster(this.gameId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.autosaveTrigger$.complete();
  }

  get dcSubtypes() {
    return this.selectedDcTypeConfig?.subTypes ?? [];
  }

  /**
   * A description belongs to one dcType/subType config row, but a type whose
   * rows all describe themselves the same way is really describing the type.
   * Show it on the type option then, so the subtype options don't repeat it.
   */
  dcTypeLabel(dcType: DcTypeConfig): string {
    const description = this.dcTypeDescriptions.get(dcType.dcType) ?? '';
    return description ? `${dcType.dcType} - ${description}` : dcType.dcType;
  }

  dcSubtypeLabel(dcSubtype: DcSubtypeConfig): string {
    const description = `${dcSubtype.description ?? ''}`.trim();
    const typeDescription =
      this.dcTypeDescriptions.get(this.dcRoster.dcType) ?? '';
    return description && description !== typeDescription
      ? `${dcSubtype.subType} - ${description}`
      : dcSubtype.subType;
  }

  get availableCollectionEvents() {
    return this.selectedDcSubtypeConfig?.collectionEvents ?? [];
  }

  onCollectionEventSearchChange(
    value: CollectionEventOption | string | null
  ): void {
    this.selectedCollectionEventOption = value;
    this.filterCollectionEventOptions(value);
  }

  private filterCollectionEventOptions(
    value: CollectionEventOption | string | null
  ): void {
    const query =
      typeof value === 'string'
        ? this.normalizeCollectionEventSearch(value)
        : '';
    if (!query) {
      this.filteredCollectionEventOptions = this.collectionEventOptions;
      return;
    }

    const queryTokens = query.split(/\s+/).filter((token) => !!token);
    this.filteredCollectionEventOptions = this.collectionEventOptions.filter(
      (option) => {
        const searchableValue = this.normalizeCollectionEventSearch(
          `${option.code} ${option.source.dcType} ${option.source.dcSubtype}`
        );
        return queryTokens.every((token) => searchableValue.includes(token));
      }
    );
  }

  private clearCollectionEventSearch(): void {
    this.selectedCollectionEventOption = null;
    this.filteredCollectionEventOptions = this.collectionEventOptions;
  }

  onDcTypeChange(dcType: string) {
    this.hasUnsavedChanges = true;
    this.dcRoster.dcType = dcType;
    const subtypeOptions =
      this.selectedDcTypeConfig?.subTypes.map((subType) => subType.subType) ??
      [];
    if (subtypeOptions.length === 1) {
      const onlySubtype = subtypeOptions[0];
      this.onDcSubtypeChange(onlySubtype);
      return;
    } else {
      this.dcRoster.dcSubtype = '';
    }
    this.clearCollectionEventSearch();
    this.emitRosterSummary();
  }

  onDcSubtypeChange(dcSubtype: string) {
    this.hasUnsavedChanges = true;
    this.dcRoster.dcSubtype = dcSubtype;
    if (this.selectedDcSubtypeConfig) {
      this.reconcileCollectionEvents();
    }
    this.clearCollectionEventSearch();
    this.emitRosterSummary();
    this.triggerAutosaveIfImmediate();
  }

  addCollectionEvent(option: CollectionEventOption | string | null) {
    const code = typeof option === 'string' ? option : option?.code;
    if (!code) {
      return;
    }
    const configSource =
      typeof option === 'string' || this.isCurrentConfigSource(option.source)
        ? null
        : option.source;
    this.dcRoster.collectionEvents.push(
      this.createCollectionEvent(code, configSource)
    );
    this.hasUnsavedChanges = true;
    this.clearCollectionEventSearch();
    this.emitRosterSummary();
    this.triggerAutosaveIfImmediate();
  }

  removeCollectionEvent(index: number) {
    this.dcRoster.collectionEvents.splice(index, 1);
    this.hasUnsavedChanges = true;
    this.emitRosterSummary();
    this.triggerAutosaveIfImmediate();
  }

  onRosterFieldChanged() {
    this.hasUnsavedChanges = true;
    this.triggerAutosaveIfImmediate();
  }

  onCollectorChanged(event: CollectionEvent, auth0Id: string) {
    const nextCollectorAuthId = auth0Id || null;
    const previousCollectorAuthId = event.collectorAuthId || null;

    event.collectorAuthId = nextCollectorAuthId;
    if (previousCollectorAuthId !== nextCollectorAuthId) {
      event.status = null;
      this.hasUnsavedChanges = true;
    }

    this.emitRosterSummary();
    this.triggerAutosaveIfImmediate();
  }

  getCollectorEligibilityContext(
    event: CollectionEvent,
    currentEventIndex = this.dcRoster.collectionEvents.indexOf(event)
  ): CollectorEligibilityContext | null {
    if (!this.gameId || !event?.code || currentEventIndex < 0) {
      return null;
    }
    const gmEvent = this.dcRoster.collectionEvents.find(
      (collectionEvent) => collectionEvent.code?.trim().toUpperCase() === 'GM'
    );
    return {
      gameId: this.gameId,
      eventCode: event.code,
      eventId: event._id ?? '',
      dcDate: this.dcRoster.dcDate ?? '',
      dcTime: this.dcRoster.dcTime ?? '',
      dcType: this.dcRoster.dcType ?? '',
      dcSubtype: this.dcRoster.dcSubtype ?? '',
      dcTypeOverride: event.dcTypeOverride ?? '',
      gmTeamLeaderAuthId: gmEvent?.collectorAuthId ?? '',
      currentEventIndex,
      draftRoster: {
        gameId: this.gameId,
        dcDate: this.dcRoster.dcDate ?? '',
        dcTime: this.dcRoster.dcTime ?? '',
        dcType: this.dcRoster.dcType ?? '',
        dcSubtype: this.dcRoster.dcSubtype ?? '',
        collectionEvents: this.dcRoster.collectionEvents.map(
          (collectionEvent) => ({
            ...(collectionEvent._id
              ? { _id: collectionEvent._id.toString() }
              : {}),
            code: collectionEvent.code ?? '',
            collectorAuthId: collectionEvent.collectorAuthId || null,
            dcTypeOverride: collectionEvent.dcTypeOverride || null,
            ...(collectionEvent.configSource
              ? { configSource: { ...collectionEvent.configSource } }
              : {}),
            status: collectionEvent.status ?? null
          })
        )
      }
    };
  }

  getStatusLabel(status: CollectionEvent['status']): string {
    return status ? this.statusLabels[status] ?? status : '—';
  }

  isValidForSave(): boolean {
    if (this.isReadOnly) {
      return true;
    }

    const hasDcType = !!this.dcRoster.dcType?.trim();
    const hasDcSubtype = !!this.dcRoster.dcSubtype?.trim();
    return !hasDcType || hasDcSubtype;
  }

  validateForSave(): boolean {
    if (this.isValidForSave()) {
      return true;
    }

    this.alertService.showWarning(DC_SUBTYPE_REQUIRED_WARNING);
    return false;
  }

  async saveDcRosterIfNeeded(
    gameId = this.gameId,
    options: { recalculate?: boolean } = {}
  ): Promise<boolean> {
    if (this.isReadOnly || !gameId || !this.dcRoster.dcType?.trim()) {
      return true;
    }
    if (!this.validateForSave()) {
      return false;
    }
    if (!this.isBillingMonthValid) {
      throw new Error('Billing Month must use YYYY-MM format.');
    }

    const rosterToSave: DcRoster = {
      ...this.dcRoster,
      gameId
    };
    const saveResponse = await firstValueFrom(
      this.dcRosterService.save(gameId, rosterToSave, options)
    );
    if (saveResponse) {
      const { warnings = [], ...savedRoster } = saveResponse;
      this.dcRoster = {
        ...this.createEmptyDcRoster(gameId),
        ...savedRoster,
        gameId,
        collectionEvents: (savedRoster.collectionEvents ?? []).map((event) => ({
          ...this.createCollectionEvent(event.code),
          ...event
        }))
      };
      this.hasUnsavedChanges = false;
      for (const warning of warnings) {
        this.alertService.showWarning(warning.message);
      }
      await this.resolveCollectorNames();
      this.emitRosterSummary();
    }
    return true;
  }

  private triggerAutosaveIfImmediate() {
    if (
      this.saveMode !== 'immediate' ||
      this.isReadOnly ||
      !this.gameId ||
      !this.dcRoster.dcType?.trim()
    ) {
      return;
    }
    if (!this.isBillingMonthValid) {
      if (!this.showRosterFields) {
        this.alertService.showError(
          'Billing Month is invalid. Please check the DC roster in Game Setup.'
        );
      }
      return;
    }

    this.autosaveTrigger$.next();
  }

  private setupAutosave() {
    this.autosaveTrigger$
      .pipe(
        debounceTime(500),
        concatMap(() => {
          this.isAutosavePending = true;
          return from(this.saveDcRosterIfNeeded()).pipe(
            catchError((error) => {
              this.alertService.showError(
                'Could not save DC roster: ' + this.getSaveErrorMessage(error)
              );
              return EMPTY;
            }),
            finalize(() => {
              this.isAutosavePending = false;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private getSaveErrorMessage(error: any): string {
    return error?.error?.message ?? error?.message ?? 'unknown error';
  }

  private async loadDcRosterConfig() {
    try {
      const config = await firstValueFrom(this.dcRosterService.getConfig());
      this.applyDcTypeConfig(config ?? []);
    } catch (error) {
      console.error('Error loading DC roster config', error);
      this.applyDcTypeConfig([]);
      this.alertService.showError(
        'Could not load DC roster config: ' + error.message
      );
    }
  }

  applyDcTypeConfig(config: DcTypeConfig[]): void {
    this.dcTypes = config;
    this.collectionEventOptions = this.buildCollectionEventOptions(config);
    this.dcTypeDescriptions = new Map(
      config.map((dcType) => [
        dcType.dcType,
        this.sharedSubtypeDescription(dcType)
      ])
    );
    this.clearCollectionEventSearch();
  }

  private sharedSubtypeDescription(dcType: DcTypeConfig): string {
    const descriptions = new Set(
      (dcType.subTypes ?? []).map((subType) =>
        `${subType.description ?? ''}`.trim()
      )
    );
    if (descriptions.size !== 1) {
      return '';
    }
    return [...descriptions][0];
  }

  private get selectedDcTypeConfig(): DcTypeConfig | undefined {
    return this.dcTypes.find(
      (dcType) => dcType.dcType === this.dcRoster.dcType
    );
  }

  private get selectedDcSubtypeConfig(): DcSubtypeConfig | undefined {
    return this.selectedDcTypeConfig?.subTypes.find(
      (subType) => subType.subType === this.dcRoster.dcSubtype
    );
  }

  private buildCollectionEventOptions(
    dcTypes: DcTypeConfig[]
  ): CollectionEventOption[] {
    const sourceConfigs = dcTypes.flatMap(({ dcType, subTypes = [] }) =>
      subTypes.map((dcSubtype) => ({ dcType, dcSubtype }))
    );

    return sourceConfigs
      .flatMap(({ dcType, dcSubtype }) =>
        (dcSubtype.collectionEvents ?? []).map((eventConfig) =>
          this.toCollectionEventOption(dcType, dcSubtype.subType, eventConfig)
        )
      )
      .filter((option): option is CollectionEventOption => option !== null)
      .sort(
        (left, right) =>
          left.code.localeCompare(right.code) ||
          left.source.dcType.localeCompare(right.source.dcType) ||
          left.source.dcSubtype.localeCompare(right.source.dcSubtype)
      );
  }

  private toCollectionEventOption(
    dcType: string,
    dcSubtype: string,
    eventConfig: DcSubtypeCollectionEventConfig | string
  ): CollectionEventOption | null {
    const code = this.getCollectionEventCode(eventConfig);
    if (!this.normalizeLookupValue(code)) {
      return null;
    }

    const details = typeof eventConfig === 'string' ? null : eventConfig;
    return {
      code,
      eventGrade: details?.eventGrade ?? null,
      effort: details?.effort ?? null,
      collectionType: details?.collectionType ?? null,
      source: { dcType, dcSubtype }
    };
  }

  private reconcileCollectionEvents(): void {
    const targetEvents = new Map<string, string>();
    for (const configEvent of this.availableCollectionEvents) {
      const code = this.getCollectionEventCode(configEvent);
      const codeKey = this.normalizeLookupValue(code);
      if (codeKey && !targetEvents.has(codeKey)) {
        targetEvents.set(codeKey, code);
      }
    }

    const retainedEvents = this.dcRoster.collectionEvents.filter((event) => {
      const isRetained = targetEvents.has(
        this.normalizeLookupValue(event.code)
      );
      if (isRetained && event.configSource) {
        event.configSource = null;
      }
      return isRetained;
    });
    const retainedCodeKeys = new Set(
      retainedEvents.map((event) => this.normalizeLookupValue(event.code))
    );
    const missingEvents = [...targetEvents]
      .filter(([codeKey]) => !retainedCodeKeys.has(codeKey))
      .map(([, code]) => this.createCollectionEvent(code));

    this.dcRoster.collectionEvents = [...retainedEvents, ...missingEvents];
  }

  private async loadDcRoster(gameId: string): Promise<void> {
    try {
      const roster = await firstValueFrom(
        this.dcRosterService.getByGameId(gameId)
      );
      this.dcRoster = {
        ...this.createEmptyDcRoster(gameId),
        ...roster,
        gameId,
        collectionEvents: (roster.collectionEvents ?? []).map((event) => ({
          ...this.createCollectionEvent(event.code),
          ...event
        }))
      };
      this.hasUnsavedChanges = false;
      this.clearCollectionEventSearch();
      await this.resolveCollectorNames();
      this.emitRosterSummary();
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.dcRoster = this.createEmptyDcRoster(gameId);
        this.hasUnsavedChanges = false;
        this.collectorNames.clear();
        this.clearCollectionEventSearch();
        this.emitRosterSummary();
        return;
      }
      console.error('Error loading DC Roster', error);
      this.dcRoster = this.createEmptyDcRoster(gameId);
      this.hasUnsavedChanges = false;
      this.collectorNames.clear();
      this.clearCollectionEventSearch();
      this.emitRosterSummary();
      this.alertService.showError('Could not load DC Roster: ' + error.message);
    }
  }

  private async resolveCollectorNames() {
    const auth0Ids = [
      ...new Set(
        this.dcRoster.collectionEvents
          .map((event) => event.collectorAuthId?.trim())
          .filter((collector) => !!collector)
      )
    ];
    if (auth0Ids.length === 0) {
      this.collectorNames.clear();
      return;
    }

    try {
      const users = await firstValueFrom(
        this.userService.findByAuth0Ids(auth0Ids)
      );
      this.collectorNames = users.reduce(
        (collectorNames, user: CollectorUser) => {
          collectorNames.set(user.auth0Id, user.name);
          return collectorNames;
        },
        new Map<string, string>()
      );
    } catch (error) {
      this.collectorNames.clear();
      console.warn('Could not resolve collector names', error);
    }
  }

  private createEmptyDcRoster(gameId = this.gameId ?? ''): DcRoster {
    return {
      gameId,
      billingMonth: '',
      dcDate: '',
      etcDate: '',
      etcTime: '',
      dcTime: '',
      region: '',
      dcType: '',
      dcSubtype: '',
      collectionEvents: []
    };
  }

  private createCollectionEvent(
    code: string,
    configSource: DcEventConfigSource | null = null
  ): CollectionEvent {
    const event: CollectionEvent = {
      code,
      duration: null,
      critical: null,
      collectorAuthId: null,
      dcTypeOverride: null,
      status: null
    };
    if (configSource) {
      event.configSource = { ...configSource };
    }
    return event;
  }

  private emitRosterSummary(): void {
    const collectionEvents = this.dcRoster.collectionEvents ?? [];
    this.rosterSummaryChange.emit({
      rosterTotal: collectionEvents.length,
      rosterAssigned: collectionEvents.filter((event) =>
        this.hasAssignedCollector(event)
      ).length
    });
  }

  private getCollectionEventCode(
    configEvent: DcSubtypeCollectionEventConfig | string
  ): string {
    if (typeof configEvent === 'string') {
      return configEvent.trim();
    }

    return `${configEvent?.code ?? ''}`.trim();
  }

  private normalizeLookupValue(value: string): string {
    return `${value ?? ''}`.trim().toUpperCase();
  }

  private normalizeCollectionEventSearch(value: string): string {
    return `${value ?? ''}`.trim().toUpperCase();
  }

  isCollectionEventOption(
    option: CollectionEventOption | string | null
  ): option is CollectionEventOption {
    return (
      !!option &&
      typeof option !== 'string' &&
      !!option.code &&
      !!option.source?.dcType &&
      !!option.source.dcSubtype
    );
  }

  readonly displayCollectionEventOption = (
    option: CollectionEventOption | string | null
  ): string => (typeof option === 'string' ? option : option?.code ?? '');

  collectionEventOptionLabel(option: CollectionEventOption): string {
    const details = [
      option.collectionType,
      option.eventGrade == null ? null : `Grade ${option.eventGrade}`,
      option.effort == null ? null : `Effort ${option.effort}`
    ].filter((value) => !!value);
    const suffix = details.length > 0 ? ` — ${details.join(', ')}` : '';
    return `${option.code} — ${option.source.dcType} / ${option.source.dcSubtype}${suffix}`;
  }

  private isCurrentConfigSource(source: DcEventConfigSource): boolean {
    return (
      this.normalizeLookupValue(source.dcType) ===
        this.normalizeLookupValue(this.dcRoster.dcType) &&
      this.normalizeLookupValue(source.dcSubtype) ===
        this.normalizeLookupValue(this.dcRoster.dcSubtype)
    );
  }

  private hasAssignedCollector(event: CollectionEvent): boolean {
    return typeof event?.collectorAuthId === 'string'
      ? event.collectorAuthId.trim().length > 0
      : !!event?.collectorAuthId;
  }
}
